import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, Checkbox, CircularProgress, IconButton, Paper, Typography } from '@material-ui/core'
import { DragIndicator, Edit } from '@material-ui/icons'
import { AgentAssignmentPool, AgentAssignmentPoolOverview, Role } from '../../../types'

interface Props {
  role: Role
  overview?: AgentAssignmentPoolOverview
  isLoading: boolean
  isUpdating: boolean
  error: string
  onSave: (executiveIds: string[], expectedNextExecutiveId: string) => void
}

const sourceLabels = {
  performance: 'Desempeño CRM',
  fallback: 'Respaldo',
  admin: 'Agregado por admin'
}

const PoolList: React.FC<{ pool: AgentAssignmentPool; showNext?: boolean }> = ({ pool, showNext = false }) => (
  <Box>
    <Typography variant="body2" color="textSecondary">
      Periodo: {pool.period} · Base: {pool.basedOnPeriod}
    </Typography>
    {pool.executives.length ? (
      pool.executives.map(executive => (
        <Box
          key={executive.executiveId}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid #eeeeee"
          py={1}
        >
          <Box>
            <Typography>
              {executive.position + 1}. {executive.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {executive.completedCrmOfficeCases} casos CRM · {sourceLabels[executive.source]}
            </Typography>
          </Box>
          {showNext && executive.executiveId === pool.nextExecutiveId ? (
            <Box color="#ff6900" fontWeight={600}>
              Próximo
            </Box>
          ) : null}
        </Box>
      ))
    ) : (
      <Typography color="textSecondary">No hay ejecutivos para mostrar.</Typography>
    )}
  </Box>
)

const AgentAssignmentPoolSection: React.FC<Props> = ({ role, overview, isLoading, isUpdating, error, onSave }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [saveRequested, setSaveRequested] = useState(false)
  const [localError, setLocalError] = useState('')
  const [draggedId, setDraggedId] = useState('')
  const updateStarted = useRef(false)

  const getCurrentOrder = () => overview?.currentPool.executives.map(executive => executive.executiveId) || []

  useEffect(() => {
    if (!overview || isEditing) return
    setSelectedIds(overview.currentPool.executives.map(executive => executive.executiveId))
  }, [overview, isEditing])

  useEffect(() => {
    if (!saveRequested) return
    if (isUpdating) {
      updateStarted.current = true
      return
    }
    if (!updateStarted.current) return
    if (!error) setIsEditing(false)
    updateStarted.current = false
    setSaveRequested(false)
  }, [error, isUpdating, saveRequested])

  if (role !== Role.admin) return null

  const startEditing = () => {
    if (!overview) return
    setSelectedIds(getCurrentOrder())
    setLocalError('')
    setIsEditing(true)
  }

  const cancelEditing = () => {
    if (overview) setSelectedIds(getCurrentOrder())
    setLocalError('')
    setIsEditing(false)
  }

  const toggleExecutive = (executiveId: string) => {
    setLocalError('')
    setSelectedIds(current =>
      current.includes(executiveId) ? current.filter(id => id !== executiveId) : [...current, executiveId]
    )
  }

  const moveToExecutivePosition = (executiveId: string, targetExecutiveId: string) => {
    setSelectedIds(current => {
      if (executiveId === targetExecutiveId) return current
      const currentIndex = current.indexOf(executiveId)
      const targetIndex = current.indexOf(targetExecutiveId)
      if (currentIndex < 0 || targetIndex < 0) return current
      const reordered = [...current]
      reordered.splice(currentIndex, 1)
      reordered.splice(targetIndex, 0, executiveId)
      return reordered
    })
  }

  const save = () => {
    if (!selectedIds.length || !overview) {
      setLocalError('El pool debe incluir al menos un ejecutivo.')
      return
    }
    updateStarted.current = false
    setSaveRequested(true)
    onSave(selectedIds, overview.currentPool.nextExecutiveId)
  }

  const availableById = new Map(overview?.availableExecutives.map(executive => [executive.executiveId, executive]))
  const currentById = new Map(overview?.currentPool.executives.map(executive => [executive.executiveId, executive]))
  const unselectedExecutives = overview?.availableExecutives.filter(
    executive => !selectedIds.includes(executive.executiveId)
  )

  return (
    <Paper
      style={{
        border: '1px solid #ff6900',
        borderRadius: '1em',
        padding: '1.5em'
      }}
    >
      <Typography variant="h5">Asignación de ejecutivos a casos CRM</Typography>
      <Typography color="textSecondary">
        Ejecutivos que reciben los casos registrados por usuarios con rol agente.
      </Typography>

      {isLoading && !overview ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress aria-label="Cargando pool CRM" />
        </Box>
      ) : null}

      {error && !isEditing ? (
        <Box color="#b00020" mt={2} role="alert">
          {error}
        </Box>
      ) : null}

      {overview ? (
        <Box className="agent-pool-columns" mt={3}>
          <Box minWidth={0}>
            <Box display="flex" alignItems="center">
              <Typography variant="h6">Asignación actual</Typography>
              {!isEditing ? (
                <IconButton
                  aria-label="Editar asignación actual"
                  onClick={startEditing}
                  style={{ color: '#ff6900', marginLeft: '0.35em' }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              ) : null}
            </Box>

            {!isEditing ? (
              <PoolList pool={overview.currentPool} showNext />
            ) : (
              <>
                <Typography variant="body2" color="textSecondary">
                  Periodo: {overview.currentPool.period} · Base: {overview.currentPool.basedOnPeriod}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Arrastra los ejecutivos seleccionados para cambiar el orden. Si el turno no avanza mientras editas, la
                  primera fila recibirá el próximo caso.
                </Typography>
                <Box display="flex" flexDirection="column">
                  {selectedIds.map((executiveId, index) => {
                    const executive = availableById.get(executiveId)
                    const currentExecutive = currentById.get(executiveId)
                    const name = executive?.name || currentExecutive?.name || 'Ejecutivo'
                    return (
                      <Box
                        key={executiveId}
                        data-testid={`ordered-executive-${executiveId}`}
                        draggable={!isUpdating}
                        display="flex"
                        alignItems="center"
                        width="100%"
                        borderBottom="1px solid #eeeeee"
                        py={0.5}
                        onDragStart={event => {
                          if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
                          setDraggedId(executiveId)
                        }}
                        onDragOver={event => event.preventDefault()}
                        onDrop={event => {
                          event.preventDefault()
                          if (draggedId) moveToExecutivePosition(draggedId, executiveId)
                          setDraggedId('')
                        }}
                        onDragEnd={() => setDraggedId('')}
                      >
                        <Checkbox
                          checked
                          disabled={isUpdating}
                          onChange={() => toggleExecutive(executiveId)}
                          color="primary"
                          inputProps={{ 'aria-label': name }}
                        />
                        <DragIndicator color="disabled" aria-hidden="true" />
                        <Box flexGrow={1} ml={0.5}>
                          <Typography>
                            {index + 1}. {name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {currentExecutive
                              ? `${currentExecutive.completedCrmOfficeCases} casos CRM · ${
                                  sourceLabels[currentExecutive.source]
                                }`
                              : 'Se agregará al pool'}
                          </Typography>
                          {index === 0 ? (
                            <Typography variant="caption" style={{ color: '#ff6900', display: 'block' }}>
                              Próximo al guardar si la rotación no ha avanzado
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    )
                  })}
                  {unselectedExecutives?.map(executive => (
                    <Box
                      key={executive.executiveId}
                      display="flex"
                      alignItems="center"
                      width="100%"
                      borderBottom="1px solid #eeeeee"
                      py={0.5}
                    >
                      <Checkbox
                        checked={false}
                        disabled={isUpdating}
                        onChange={() => toggleExecutive(executive.executiveId)}
                        color="primary"
                        inputProps={{ 'aria-label': executive.name }}
                      />
                      <Box ml={0.5}>
                        <Typography color="textSecondary">{executive.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Disponible para agregar
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                {localError || error ? (
                  <Box color="#b00020" mt={1} role="alert">
                    {localError || error}
                  </Box>
                ) : null}
                <Box mt={2}>
                  <Button variant="contained" color="primary" disabled={isUpdating} onClick={save}>
                    {isUpdating ? 'Guardando…' : 'Guardar'}
                  </Button>
                  <Button disabled={isUpdating} onClick={cancelEditing}>
                    Cancelar
                  </Button>
                </Box>
              </>
            )}
          </Box>

          <Box minWidth={0}>
            <Typography variant="h6">Estimación próximo mes</Typography>
            <Typography variant="caption" color="textSecondary">
              Proyección no definitiva calculada con la información disponible actualmente.
            </Typography>
            <PoolList pool={overview.nextMonthEstimate} />
          </Box>
        </Box>
      ) : null}
    </Paper>
  )
}

export default AgentAssignmentPoolSection
